import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

interface PoComboOption {
  value: string | number;
  label?: string;
}
interface PoComboFilter {
  getFilteredData(params: unknown, filterParams?: unknown): Observable<PoComboOption[]>;
  getObjectByValue(value: string | number, filterParams?: unknown): Observable<PoComboOption>;
}

export interface EmpresaComboItem {
  id?: number;
  codDominio?: number | null;
  CodDominio?: number | null;
  nome?: string;
  Nome?: string;
}

/** Extrai só o código Domínio (codi_emp). Nunca usa o Id interno do Ecco. */
function resolverCodDominio(item: EmpresaComboItem): number | null {
  const raw = item.codDominio ?? item.CodDominio;
  if (raw == null) return null;
  const code = Number(raw);
  return !Number.isNaN(code) && code > 0 ? code : null;
}

function resolverNome(item: EmpresaComboItem): string {
  return (item.nome ?? item.Nome ?? '').trim();
}

@Injectable({ providedIn: 'root' })
export class EmpresaComboFilterService implements PoComboFilter {
  private readonly api = `${environment.apiUrl}/v1/empresas`;

  constructor(private http: HttpClient) {}

  getFilteredData(params: { value?: string; filter?: string; search?: string }, _filterParams?: unknown): Observable<PoComboOption[]> {
    const term = (params?.value ?? params?.filter ?? params?.search ?? '') as string;
    if (typeof term !== 'string' || !term.trim()) {
      return of([]);
    }
    return this.http
      .get<EmpresaComboItem[]>(`${this.api}/search`, { params: { term: term.trim(), limit: 20 } })
      .pipe(
        map((items) => {
          const seen = new Set<number>();
          return (items || [])
            .map((item) => {
              const code = resolverCodDominio(item);
              if (code == null || seen.has(code)) return null;
              seen.add(code);
              const nome = resolverNome(item);
              return {
                value: code,
                label: nome ? `${code} - ${nome}` : String(code)
              } as PoComboOption;
            })
            .filter((opt): opt is PoComboOption => opt != null);
        })
      );
  }

  getObjectByValue(value: string | number, _filterParams?: unknown): Observable<PoComboOption> {
    const fallback: PoComboOption = { value: value as number, label: String(value) };
    if (value === '' || value === null || value === undefined) {
      return of(fallback);
    }
    const codDominio = typeof value === 'string' ? parseInt(value, 10) : value;
    if (Number.isNaN(codDominio) || codDominio <= 0) {
      return of(fallback);
    }

    return this.http
      .get<EmpresaComboItem[]>(`${this.api}/search`, { params: { term: String(codDominio), limit: 5 } })
      .pipe(
        map((items) => {
          const item = (items || []).find((x) => resolverCodDominio(x) === codDominio);
          if (!item) return fallback;
          const code = resolverCodDominio(item)!;
          const nome = resolverNome(item);
          return {
            value: code,
            label: nome ? `${code} - ${nome}` : String(code)
          };
        }),
        catchError(() => of(fallback))
      );
  }
}
